
import { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { setAlert, siteboardAction, } from '../actions/defaultActions';
import { 
  Project, EDIT_PROJECT, UUID, SiteboardApiValidator, getSubscriptionOwnerId, SET_ALERT, Email,
  GET_PROJECT_SCHEDULE_CONTACTS, DELETE_PROJECT, SET_DELETE_PROJECT_DIALOGUE_ID, SET_EDIT_PROJECT_DIALOGUE_ID,
} from '../actions/types';
import { keyCountInObject, } from '../Util';

import PopupWindow from './PopupWindow';
import ProjectMembers from './ProjectMembers';
import CollapsibleContainer from './CollapsibleContainer';
import PushButton from './PushButton';
import TabSelector from './TabSelector';
import ProjectContacts from './ProjectContacts';

// icon imports
import {
  FolderKanbanIcon,
  HashIcon,
  Building2Icon,
  MapPinIcon,
  CalendarDaysIcon,
  Clock3Icon,
  HardHatIcon,
  PhoneIcon,
  UserRoundIcon,
  SaveIcon,
  RotateCcwIcon,
  UsersIcon,
  SlidersHorizontalIcon,
  ContactRound,
} from 'lucide-react';
import TrashIcon from '../icons/trashIcon';

const PAGE_PROJECT_DETAILS = 'PAGE_PROJECT_DETAILS';
const PAGE_PROJECT_TEAM = 'PAGE_PROJECT_TEAM';
const PAGE_PROJECT_DISTRIBUTION_LIST = 'PAGE_PROJECT_DISTRIBUTION_LIST';

const inputLabelIconColor = 'text-white/85';

const EditProjectDialogue = ({ 
  _projects,
  _users,
  _userId,
  _editProjectDialogueId,
  _editProjectDialogueTabName,
  _subscriptions,
  setAlert,
  siteboardAction,
}) => {

  const selectedProject = _projects[_editProjectDialogueId];
  const selectedSubscription = selectedProject ? _subscriptions[selectedProject.subscriptionId] : null;
  const userCanEditProject = SiteboardApiValidator[EDIT_PROJECT].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  const memberCountInSelectedProject = !selectedProject ? 0 : (keyCountInObject(selectedProject.members) + keyCountInObject(selectedProject.pendingMembers));

  const [currentPage, setCurrentPage] = useState(_editProjectDialogueTabName === 'Distribution' ? PAGE_PROJECT_DISTRIBUTION_LIST : (_editProjectDialogueTabName === 'Team' ? PAGE_PROJECT_TEAM : PAGE_PROJECT_DETAILS));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [moreDetailsShowing, setMoreDetailsShowing] = useState(false);

  const [name, setName] = useState(selectedProject.name || '');
  const [jobNumber, setJobNumber] = useState(selectedProject.jobNumber || '');
  const [address, setAddress] = useState(selectedProject.address || '');
  const [customer, setCustomer] = useState(selectedProject.customer || '');
  const [dirNumber, setDirNumber] = useState( selectedProject.dirNumber || '');
  const [beginDate, setBeginDate] = useState(selectedProject.beginDate);
  const [calendarDays, setCalendarDays] = useState(selectedProject.calendarDays || '');
  const [superintendent, setSuperintendent] = useState( selectedProject.superintendent || '');
  const [superintendentPhone, setSuperintendentPhone] = useState( selectedProject.superintendentPhone || '');

  // Fetch schedule contacts from the server. If we already have them this will do nothing
  useEffect(() => {
    if(!selectedProject) return;
    siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, {projectId: selectedProject.id});
  }, [siteboardAction, selectedProject]);

  useEffect(() => {
    setHasUnsavedChanges(name !== selectedProject.name
      || jobNumber !== selectedProject.jobNumber
      || address !== selectedProject.address
      || customer !== selectedProject.customer
      || dirNumber !== selectedProject.dirNumber
      || beginDate !== selectedProject.beginDate
      || calendarDays !== selectedProject.calendarDays
      || superintendent !== selectedProject.superintendent
      || superintendentPhone !== selectedProject.superintendentPhone
    );
  }, [name, jobNumber, address, customer, dirNumber, beginDate, calendarDays, superintendent, superintendentPhone, selectedProject]);

  const getProjectAccountOwnerEmail = () => {
    if(!selectedProject) return '';

    const projectSubscriptionId = selectedProject.subscriptionId;
    if (projectSubscriptionId === null) return '';
    if(!(projectSubscriptionId in _subscriptions)) return '';
    const subscriptionOwnerId = getSubscriptionOwnerId(_subscriptions[projectSubscriptionId]);
    if(!(subscriptionOwnerId in _users)) return '';
    return _users[subscriptionOwnerId].email; 
  };

  const saveProjectInfo = () => {
    try {
      if(typeof name !== 'string' || name.length === 0) throw new Error('Please enter a project name');

      const projectSubscriptionId = selectedProject.subscriptionId;

      const project = Project.validate({
        id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
        ownerId: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
        subscriptionId: projectSubscriptionId,
        members: {},
        schedules: {},
        createdTimestamp: new Date().getTime(),
        modifiedTimestamp: new Date().getTime(), 
        // attributes above here are placeholders to pass validation, they will be overwritten
        ...selectedProject,
        name: name,
        jobNumber: jobNumber.trim(),
        address: address,
        customer: customer,
        dirNumber: dirNumber,
        beginDate: beginDate,
        calendarDays: calendarDays.trim().replaceAll(/[\s,]/g, ''),
        superintendent: superintendent,
        superintendentPhone: superintendentPhone,
      });

      siteboardAction(EDIT_PROJECT, {project: project}, (output) => {});
    } catch(error) {
      setAlert(error.message); 
    }
  }

  const closeDialogue = useCallback(() => {
    if(hasUnsavedChanges && userCanEditProject) {
      setAlert('You have unsaved changes. Click "Save" or "Discard" at the bottom of this page before closing project settings.'); 
    } else {
      siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: '' });
    }
  }, [hasUnsavedChanges, setAlert, userCanEditProject, siteboardAction]);

  return (
    <PopupWindow 
      title='Project Settings'
      closeDialogue={closeDialogue}
      bgStyle='bg-neutral-800'
      isFullHeight={true}
    >
      <div>
        <div className='mb-2'>
          <TabSelector 
            tabs={[
              {
                label: "Details",
                icon: <SlidersHorizontalIcon size={18} strokeWidth={1.8} />,
              },
              {
                label: `Team (${memberCountInSelectedProject})`,
                icon: <UsersIcon size={20} strokeWidth={1.8} />,
              },
              {
                label: `Distribution (${selectedProject.contactCount})`,
                icon: <ContactRound size={20} strokeWidth={1.8} />,
              },
            ]} 
            initialTab={_editProjectDialogueTabName === 'Distribution' ? 2 : (_editProjectDialogueTabName === 'Team' ? 1 : 0)} 
            onChange={(tabIndex) => {  
              setCurrentPage(tabIndex === 2 ? PAGE_PROJECT_DISTRIBUTION_LIST : (tabIndex === 1 ? PAGE_PROJECT_TEAM : PAGE_PROJECT_DETAILS ));
            }}
          />
        </div>
        <div className={`flex w-full flex-col gap-6 text-white max-w-screen-lg`}>
          {/* Project information */}
          {currentPage === PAGE_PROJECT_DETAILS &&
            <div className="flex w-full flex-col gap-5 animate-left-fade">
              <div className='w-[90vw] max-w-full' />

              {/* Form content */}
              <div className="flex flex-col gap-5">

                {/* Project Name */}
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="project-name"
                    className="flex items-center gap-2 text-sm font-semibold text-white/85"
                  >
                    <FolderKanbanIcon size={16} className={inputLabelIconColor} />
                    Project Name
                  </label>

                  <input
                    id="project-name"
                    type="text"
                    placeholder="e.g. Downtown Office Renovation"
                    disabled={!userCanEditProject}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="project-detail-input"
                  />
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-job-number" className="project-detail-label">
                      <HashIcon size={15} className={inputLabelIconColor} />
                      Job Number
                    </label>
                    <input
                      id="edit-job-number"
                      type="text"
                      value={jobNumber}
                      disabled={!userCanEditProject}
                      onChange={(event) => setJobNumber(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-customer" className="project-detail-label">
                      <Building2Icon size={15} className={inputLabelIconColor} />
                      Customer
                    </label>
                    <input
                      id="edit-customer"
                      type="text"
                      value={customer}
                      disabled={!userCanEditProject}
                      onChange={(event) => setCustomer(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label htmlFor="edit-address" className="project-detail-label">
                      <MapPinIcon size={15} className={inputLabelIconColor} />
                      Project Address
                    </label>
                    <textarea
                      id="edit-address"
                      rows={2}
                      value={address}
                      disabled={!userCanEditProject}
                      onChange={(event) => setAddress(event.target.value)}
                      className="project-detail-input resize-y"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-begin-date" className="project-detail-label">
                      <CalendarDaysIcon size={15} className={inputLabelIconColor} />
                      Notice to Proceed Date
                    </label>
                    <input
                      id="edit-begin-date"
                      type="text"
                      placeholder="MM/DD/YYYY"
                      value={beginDate}
                      disabled={!userCanEditProject}
                      onChange={(event) => setBeginDate(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-calendar-days" className="project-detail-label">
                      <Clock3Icon size={15} className={inputLabelIconColor} />
                      Consecutive Calendar Days
                    </label>
                    <input
                      id="edit-calendar-days"
                      type="text"
                      value={calendarDays}
                      disabled={!userCanEditProject}
                      onChange={(event) => setCalendarDays(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-superintendent" className="project-detail-label">
                      <HardHatIcon size={15} className={inputLabelIconColor} />
                      Superintendent
                    </label>
                    <input
                      id="edit-superintendent"
                      type="text"
                      value={superintendent}
                      disabled={!userCanEditProject}
                      onChange={(event) => setSuperintendent(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="edit-superintendent-phone" className="project-detail-label">
                      <PhoneIcon size={15} className={inputLabelIconColor} />
                      Superintendent Phone
                    </label>
                    <input
                      id="edit-superintendent-phone"
                      type="text"
                      value={superintendentPhone}
                      disabled={!userCanEditProject}
                      onChange={(event) => setSuperintendentPhone(event.target.value)}
                      className="project-detail-input"
                    />
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="project-detail-label">
                      <UserRoundIcon size={15} className={inputLabelIconColor} />
                      Project Account Owner
                    </label>

                    <input
                      value={getProjectAccountOwnerEmail()}
                      disabled={true}
                      className={`project-detail-input`}
                    />
                  </div>

                </div>
              </div>

              {userCanEditProject && (
                <CollapsibleContainer isOpen={hasUnsavedChanges}>
                  <div className="flex flex-col gap-3 border-t border-white/10 pt-5">

                    {/* Unsaved changes notice */}
                    <div className="
                      flex items-center gap-2
                      rounded-lg border border-[#f9a928]/20
                      bg-[#f9a928]/[0.06]
                      px-3 py-2.5
                      text-sm text-[#f9a928]
                    ">
                      <span className="relative flex h-2 w-2 shrink-0">
                        <span className="
                          absolute inline-flex h-full w-full
                          animate-ping rounded-full bg-[#f9a928]/50
                          motion-reduce:animate-none
                        " />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#f9a928]" />
                      </span>
                      You have unsaved changes
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        disabled={!hasUnsavedChanges}
                        onClick={() => {
                          setName(selectedProject.name);
                          setJobNumber(selectedProject.jobNumber);
                          setAddress(selectedProject.address);
                          setCustomer(selectedProject.customer);
                          setDirNumber(selectedProject.dirNumber);
                          setBeginDate(selectedProject.beginDate);
                          setCalendarDays(selectedProject.calendarDays);
                          setSuperintendent(selectedProject.superintendent);
                          setSuperintendentPhone(selectedProject.superintendentPhone);
                        }}
                        className="
                          flex flex-1 items-center justify-center gap-2
                          rounded-lg border border-white/15
                          bg-white/5 px-4 py-3
                          text-sm font-semibold text-white/75
                          transition-all duration-200
                          hover:border-white/30 hover:bg-white/10 hover:text-white
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                      >
                        <RotateCcwIcon size={16} />
                        Discard
                      </button>

                      <button
                        type="button"
                        disabled={!hasUnsavedChanges}
                        onClick={saveProjectInfo}
                        className="
                          group flex flex-[2] items-center justify-center gap-2
                          rounded-lg bg-[#471cff] px-4 py-3
                          text-sm font-semibold text-white
                          shadow-[0_4px_20px_rgba(71,28,255,0.2)]
                          transition-all duration-200
                          hover:bg-[#603aff]
                          hover:shadow-[0_6px_25px_rgba(71,28,255,0.3)]
                          active:scale-[0.98]
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a994ff]
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                      >
                        <SaveIcon
                          size={17}
                          className="transition-transform duration-300 group-hover:-translate-y-0.5"
                        />
                        <span className="hidden sm:inline">Save Project Information</span>
                        <span className="sm:hidden">Save Changes</span>
                      </button>
                    </div>
                  </div>
                </CollapsibleContainer>
              )}

              {/* Delete project button */}
              {SiteboardApiValidator[DELETE_PROJECT].isUserIdAuthorized(_userId, selectedSubscription, selectedProject) && 
                <div>
                  <PushButton 
                    isInline={true}
                    textColor={'white'}
                    bgColor={'bg-red-800'}
                    hoverBgColor={'bg-red-600'}
                    onClick={() => {
                      siteboardAction(SET_DELETE_PROJECT_DIALOGUE_ID, {projectId: selectedProject.id});
                    }}
                  >
                    <div className='flex items-center gap-x-1.5'>
                      <TrashIcon color={'white'} />
                      <p className=''>Delete Project</p>
                    </div>
                  </PushButton>
                </div>
              }
            </div>
          }

          {/* Project members */}
          {currentPage === PAGE_PROJECT_TEAM && (
            <section className="flex flex-col gap-4 animate-right-fade">
              <div className='w-[90vw] max-w-full' />
              <ProjectMembers project={selectedProject} />
            </section>
          )}

          {/* Project distribution list */}
          {currentPage === PAGE_PROJECT_DISTRIBUTION_LIST && (
            <section className="flex flex-col gap-4 animate-right-fade">
              <div className='w-[90vw] max-w-full' />
              <ProjectContacts projectId={selectedProject.id} subscriptionId={selectedSubscription.id} />
            </section>
          )}
        </div>
      </div>

    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _users: state.serverSynced.users,
  _editProjectDialogueId: state.localEphemeral.editProjectDialogueId,
  _editProjectDialogueTabName: state.localEphemeral.editProjectDialogueTabName,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {setAlert, siteboardAction, })(EditProjectDialogue);
